// -----------------------------------------------------------------------------
// Maison Dodji — Project Space chapter schema (single source of truth)
//
// Each chapter has:
//   type       — one of CHAPTER_ORDER
//   status     — "draft" | "complete" (Maison's internal workflow marker)
//   shared     — boolean (visible to the client in the Project Space)
//   observations — first-person Maison note attached to this chapter
//   fields     — type-specific structured content
//
// The editor (Atelier), the client-facing Project Space and the printed
// Architectural Brief all read from this schema.
// -----------------------------------------------------------------------------

export const CHAPTER_ORDER = [
  "welcome",
  "projectOwner",
  "residence",
  "architecture",
  "watchCollection",
  "existingSafe",
  "futureCollection",
  "lifestyle",
  "designReferences",
  "architect",
  "interiorDesigner",
  "photos",
  "measurements",
  "technicalConstraints",
  "maisonObservations",
  "maisonQuestions",
  "projectConclusion",
];

// French internal labels used by the Maison inside /atelier and the printed brief.
export const CHAPTER_LABELS_FR = {
  welcome: "Bienvenue dans le Project Space",
  projectOwner: "Le commanditaire",
  residence: "La résidence",
  architecture: "L'architecture",
  watchCollection: "La collection horlogère",
  existingSafe: "Le coffre existant",
  futureCollection: "La collection à venir",
  lifestyle: "Le mode de vie",
  designReferences: "Références de design",
  architect: "L'architecte",
  interiorDesigner: "L'architecte d'intérieur",
  photos: "Photographies des lieux",
  measurements: "Mesures et dimensions",
  technicalConstraints: "Contraintes techniques",
  maisonObservations: "Observations de la Maison",
  maisonQuestions: "Questions de la Maison",
  projectConclusion: "Conclusion du projet",
};

// The Maison's own preface for each chapter, spoken in the first person.
// Rendered as a quiet italic paragraph both in /atelier and the client Space.
export const CHAPTER_PURPOSE_FR = {
  welcome:
    "La Maison ouvre ici un espace dédié à votre projet. Chaque chapitre s'écrit à la mesure du lieu et de la pièce à venir.",
  projectOwner:
    "La Maison souhaite reconnaître la personne pour laquelle la pièce est conçue.",
  residence:
    "La Maison a besoin de comprendre le lieu qui accueillera la pièce.",
  architecture:
    "La Maison examine les volumes, la lumière et les matériaux du lieu.",
  watchCollection:
    "La Maison prend connaissance des montres déjà présentes.",
  existingSafe:
    "La Maison consigne le coffre actuel, ses limites et son emplacement.",
  futureCollection:
    "La Maison anticipe les pièces à venir, celles qu'il faudra accueillir.",
  lifestyle:
    "La Maison écoute le rythme du quotidien, les voyages, les usages.",
  designReferences:
    "La Maison rassemble les références visuelles évoquées ensemble.",
  architect:
    "La Maison identifie l'architecte associé au projet, s'il existe.",
  interiorDesigner:
    "La Maison identifie l'architecte d'intérieur, s'il existe.",
  photos:
    "La Maison rassemble les photographies du lieu telles qu'il est aujourd'hui.",
  measurements:
    "La Maison relève les dimensions utiles à la conception de la pièce.",
  technicalConstraints:
    "La Maison consigne les contraintes techniques du lieu.",
  maisonObservations:
    "La Maison écrit ici ce qu'elle a compris, à sa manière.",
  maisonQuestions:
    "La Maison adresse au commanditaire les questions qui restent à trancher.",
  projectConclusion:
    "La Maison clôt le Project Space en résumant les décisions arrêtées.",
};

export const CHAPTER_PURPOSE_EN = {
  welcome:
    "The Maison opens here a space dedicated to your project. Each chapter is written to the measure of the place and the piece to come.",
  projectOwner:
    "The Maison wishes to recognise the person for whom the piece is conceived.",
  residence: "The Maison needs to understand the place that will host the piece.",
  architecture:
    "The Maison studies the volumes, the light and the materials of the place.",
  watchCollection: "The Maison takes note of the watches already present.",
  existingSafe:
    "The Maison records the current safe, its limits and its location.",
  futureCollection:
    "The Maison anticipates the pieces to come, those that will need a home.",
  lifestyle:
    "The Maison listens to the rhythm of daily life, travels, and uses.",
  designReferences:
    "The Maison gathers the visual references discussed together.",
  architect: "The Maison identifies the architect involved in the project, if any.",
  interiorDesigner:
    "The Maison identifies the interior designer, if any.",
  photos:
    "The Maison gathers the photographs of the place as it stands today.",
  measurements:
    "The Maison notes the dimensions useful to the design of the piece.",
  technicalConstraints:
    "The Maison records the technical constraints of the place.",
  maisonObservations:
    "The Maison writes here what it has understood, in its own words.",
  maisonQuestions:
    "The Maison addresses to the owner the questions still to be resolved.",
  projectConclusion:
    "The Maison closes the Project Space by summarising the decisions taken.",
};

// Type-specific structured field schema.
// kind: "text" (single line), "textarea", "select", "list" (repeatable items)
export const CHAPTER_FIELDS = {
  welcome: [
    { key: "body", kind: "textarea", labelKey: "body" },
  ],
  projectOwner: [
    { key: "fullName", kind: "text", labelKey: "fullName" },
    { key: "city", kind: "text", labelKey: "city" },
    { key: "contact", kind: "text", labelKey: "contact" },
    { key: "background", kind: "textarea", labelKey: "background" },
  ],
  residence: [
    { key: "address", kind: "text", labelKey: "address" },
    { key: "style", kind: "text", labelKey: "style" },
    { key: "era", kind: "text", labelKey: "era" },
    { key: "rooms", kind: "textarea", labelKey: "rooms" },
    { key: "atmosphere", kind: "textarea", labelKey: "atmosphere" },
  ],
  architecture: [
    { key: "volumes", kind: "textarea", labelKey: "volumes" },
    { key: "materials", kind: "textarea", labelKey: "materials" },
    { key: "light", kind: "textarea", labelKey: "light" },
    { key: "ambiance", kind: "textarea", labelKey: "ambiance" },
  ],
  watchCollection: [
    { key: "summary", kind: "textarea", labelKey: "summary" },
    {
      key: "items", kind: "list", labelKey: "reference",
      itemFields: [
        { key: "brand", kind: "text", labelKey: "brand" },
        { key: "reference", kind: "text", labelKey: "reference" },
        { key: "notes", kind: "text", labelKey: "notes" },
      ],
    },
  ],
  existingSafe: [
    {
      key: "hasSafe", kind: "select", labelKey: "hasSafe",
      options: [
        { v: "yes", labelKey: "yes" },
        { v: "no", labelKey: "no" },
        { v: "na", labelKey: "na" },
      ],
    },
    { key: "brand", kind: "text", labelKey: "brand" },
    { key: "model", kind: "text", labelKey: "model" },
    { key: "capacity", kind: "text", labelKey: "capacity" },
    { key: "location", kind: "text", labelKey: "location" },
    { key: "limitations", kind: "textarea", labelKey: "limitations" },
  ],
  futureCollection: [
    { key: "intent", kind: "textarea", labelKey: "intent" },
    { key: "horizon", kind: "text", labelKey: "horizon" },
    {
      key: "items", kind: "list", labelKey: "reference",
      itemFields: [
        { key: "brand", kind: "text", labelKey: "brand" },
        { key: "reference", kind: "text", labelKey: "reference" },
      ],
    },
  ],
  lifestyle: [
    { key: "rhythm", kind: "textarea", labelKey: "rhythm" },
    { key: "travel", kind: "textarea", labelKey: "travel" },
    { key: "interests", kind: "textarea", labelKey: "interests" },
  ],
  designReferences: [
    { key: "summary", kind: "textarea", labelKey: "summary" },
    {
      key: "items", kind: "list", labelKey: "imageUrl",
      itemFields: [
        { key: "imageUrl", kind: "text", labelKey: "imageUrl" },
        { key: "caption", kind: "text", labelKey: "caption" },
      ],
    },
  ],
  architect: [
    { key: "name", kind: "text", labelKey: "name" },
    { key: "agency", kind: "text", labelKey: "agency" },
    { key: "contact", kind: "text", labelKey: "contact" },
    { key: "notes", kind: "textarea", labelKey: "notes" },
  ],
  interiorDesigner: [
    { key: "name", kind: "text", labelKey: "name" },
    { key: "agency", kind: "text", labelKey: "agency" },
    { key: "contact", kind: "text", labelKey: "contact" },
    { key: "notes", kind: "textarea", labelKey: "notes" },
  ],
  photos: [
    {
      key: "items", kind: "list", labelKey: "imageUrl",
      itemFields: [
        { key: "imageUrl", kind: "text", labelKey: "imageUrl" },
        { key: "caption", kind: "text", labelKey: "caption" },
      ],
    },
  ],
  measurements: [
    { key: "notes", kind: "textarea", labelKey: "notes" },
    {
      key: "items", kind: "list", labelKey: "label",
      itemFields: [
        { key: "label", kind: "text", labelKey: "label" },
        { key: "dimensions", kind: "text", labelKey: "dimensions" },
      ],
    },
  ],
  technicalConstraints: [
    { key: "access", kind: "textarea", labelKey: "access" },
    { key: "electrical", kind: "textarea", labelKey: "electrical" },
    { key: "humidity", kind: "textarea", labelKey: "humidity" },
    { key: "security", kind: "textarea", labelKey: "security" },
    { key: "other", kind: "textarea", labelKey: "other" },
  ],
  maisonObservations: [
    { key: "body", kind: "textarea", labelKey: "body" },
  ],
  maisonQuestions: [
    {
      key: "items", kind: "list", labelKey: "prompt",
      itemFields: [
        { key: "prompt", kind: "textarea", labelKey: "prompt" },
      ],
    },
  ],
  projectConclusion: [
    { key: "body", kind: "textarea", labelKey: "body" },
    { key: "nextSteps", kind: "textarea", labelKey: "nextSteps" },
  ],
};

export function emptyChapter(type) {
  const spec = CHAPTER_FIELDS[type] || [];
  const fields = {};
  spec.forEach((f) => {
    if (f.kind === "list") fields[f.key] = [];
    else fields[f.key] = "";
  });
  return {
    type,
    status: "draft",
    shared: false,
    observations: "",
    fields,
  };
}

export function migrateChapter(c) {
  if (!c || typeof c !== "object") return c;
  // Backwards compatibility: legacy chapters had { type, body, published }.
  if (c.fields) return c;
  const legacyType = CHAPTER_ORDER.includes(c.type) ? c.type : "welcome";
  const base = emptyChapter(legacyType);
  base.shared = !!(c.shared ?? c.published);
  if (typeof c.body === "string" && "body" in base.fields) {
    base.fields.body = c.body;
  }
  if (c.observations) base.observations = c.observations;
  if (c.status) base.status = c.status;
  return base;
}

// -----------------------------------------------------------------------------
// Private Commissions — Maison Dodji Paris editorial pages.
//
// Distinct from the Project Space chapter set above. Commissions are the public
// editorial record; Project Spaces are the confidential client environment.
// The two never share data or schema — commissions are stored on their own
// `commissionChapters` field of the space document.
// -----------------------------------------------------------------------------

export const COMMISSION_STATUSES = [
  "commission_opened",
  "design_study",
  "design_proof_approved",
  "preparation",
  "personalisation",
  "assembly",
  "final_inspection",
  "completed",
  "delivered",
];

export const COMMISSION_STATUS_LABELS = {
  fr: {
    commission_opened: "Commande ouverte",
    design_study: "Étude de conception",
    design_proof_approved: "Design Proof approuvé",
    preparation: "Préparation",
    personalisation: "Personnalisation",
    assembly: "Assemblage",
    final_inspection: "Contrôle final",
    completed: "Achevée",
    delivered: "Livrée",
  },
  en: {
    commission_opened: "Commission opened",
    design_study: "Design study",
    design_proof_approved: "Design Proof approved",
    preparation: "Preparation",
    personalisation: "Personalisation",
    assembly: "Assembly",
    final_inspection: "Final inspection",
    completed: "Completed",
    delivered: "Delivered",
  },
};

export const COMMISSION_CHAPTER_ORDER = [
  "commission",
  "designIntention",
  "colourStudy",
  "preparation",
  "personalisation",
  "details",
  "finalAssembly",
  "completedPiece",
  "installation",
];

export const COMMISSION_CHAPTER_LABELS = {
  fr: {
    commission: "La commande",
    designIntention: "L'intention de conception",
    colourStudy: "L'étude de la couleur",
    preparation: "La préparation",
    personalisation: "La personnalisation",
    details: "Les détails",
    finalAssembly: "L'assemblage final",
    completedPiece: "La pièce achevée",
    installation: "L'installation ou la livraison",
  },
  en: {
    commission: "The commission",
    designIntention: "The design intention",
    colourStudy: "The colour study",
    preparation: "Preparation",
    personalisation: "Personalisation",
    details: "Details",
    finalAssembly: "Final assembly",
    completedPiece: "The completed piece",
    installation: "Installation or delivery",
  },
};

// Every commission chapter shares the same shape: date, body, caption, images[],
// published. No type-specific field variations — the commission page is a single
// editorial sequence written by the Maison.
export function emptyCommissionChapter(type) {
  return {
    type,
    date: "",
    body: "",
    caption: "",
    images: [],       // [{ url, caption }]
    published: false, // Maison must explicitly authorise public visibility
  };
}

